/**
 * Backfill House Points - Recalculate housePoints from this week's recognitions + mentions
 *
 * Usage: await window.backfillHousePoints(schoolId)
 *   or:  await window.backfillHousePoints(schoolId, '2026-W08') // specific week
 */

import { collection, getDocs, query, where, doc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

function getWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function getWeekStart(weekKeyOrNull) {
  if (!weekKeyOrNull) {
    // Current week's Monday
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    return monday;
  }
  // Parse YYYY-WNN
  const [yearStr, weekStr] = weekKeyOrNull.split('-W');
  const year = parseInt(yearStr);
  const week = parseInt(weekStr);
  // Jan 4 is always in week 1
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export async function backfillHousePoints(schoolId, specificWeekKey = null) {
  if (!schoolId) {
    console.error('Usage: await window.backfillHousePoints(schoolId)');
    return;
  }

  const weekStart = getWeekStart(specificWeekKey);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  const weekKey = specificWeekKey || getWeekKey(new Date());

  console.log(`🏠 Backfilling house points for ${weekKey}`);
  console.log(`   Date range: ${weekStart.toLocaleDateString()} → ${weekEnd.toLocaleDateString()}`);

  // 1. Fetch school houses
  const housesSnap = await getDocs(collection(db, 'schools', schoolId, 'houses'));
  const houses = housesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  console.log(`   Houses: ${houses.map(h => h.name).join(', ')}`);

  if (houses.length === 0) {
    console.warn('No houses configured for this school.');
    return;
  }

  // Build name → id map (case-insensitive)
  const houseMap = new Map();
  houses.forEach(h => {
    houseMap.set((h.name || '').toLowerCase().trim(), h.id);
  });

  // 2. Fetch all students to get house assignments
  const studentsSnap = await getDocs(collection(db, 'schools', schoolId, 'students'));
  const studentHouseMap = new Map(); // studentId → houseId
  let assigned = 0;
  studentsSnap.docs.forEach(d => {
    const data = d.data();
    if (data.house) {
      const houseId = houseMap.get(data.house.toLowerCase().trim());
      if (houseId) {
        studentHouseMap.set(d.id, houseId);
        assigned++;
      }
    }
  });
  console.log(`   Students with house: ${assigned}/${studentsSnap.size}`);

  // 3. Fetch recognitions from this week
  const startTs = Timestamp.fromDate(weekStart);
  const endTs = Timestamp.fromDate(weekEnd);

  const recsSnap = await getDocs(query(
    collection(db, 'schools', schoolId, 'recognitions'),
    where('createdAt', '>=', startTs),
    where('createdAt', '<', endTs)
  ));
  console.log(`   Recognitions this week: ${recsSnap.size}`);

  // 4. Fetch mentions from this week
  const mentsSnap = await getDocs(query(
    collection(db, 'schools', schoolId, 'mentions'),
    where('createdAt', '>=', startTs),
    where('createdAt', '<', endTs)
  ));
  console.log(`   Mentions this week: ${mentsSnap.size}`);

  // 5. Aggregate by house
  const houseTotals = {}; // houseId → { positive, negative }
  houses.forEach(h => { houseTotals[h.id] = { positive: 0, negative: 0 }; });

  let recsMapped = 0;
  recsSnap.docs.forEach(d => {
    const data = d.data();
    if (data.active === false) return;
    const houseId = data.houseId || studentHouseMap.get(data.studentId);
    if (houseId && houseTotals[houseId]) {
      houseTotals[houseId].positive += (data.points || 1);
      recsMapped++;
    }
  });

  let mentsMapped = 0;
  mentsSnap.docs.forEach(d => {
    const data = d.data();
    const houseId = data.houseId || studentHouseMap.get(data.studentId);
    if (houseId && houseTotals[houseId]) {
      houseTotals[houseId].negative += 1; // 1 point per mention
      mentsMapped++;
    }
  });

  console.log(`   Recognitions mapped to houses: ${recsMapped}`);
  console.log(`   Mentions mapped to houses: ${mentsMapped}`);

  // 6. Also compute all-time totals from ALL recognitions/mentions
  const allRecsSnap = await getDocs(query(
    collection(db, 'schools', schoolId, 'recognitions')
  ));
  const allMentsSnap = await getDocs(query(
    collection(db, 'schools', schoolId, 'mentions')
  ));

  const allTimeTotals = {};
  houses.forEach(h => { allTimeTotals[h.id] = { positive: 0, negative: 0 }; });

  allRecsSnap.docs.forEach(d => {
    const data = d.data();
    if (data.active === false) return;
    const houseId = data.houseId || studentHouseMap.get(data.studentId);
    if (houseId && allTimeTotals[houseId]) {
      allTimeTotals[houseId].positive += (data.points || 1);
    }
  });

  allMentsSnap.docs.forEach(d => {
    const data = d.data();
    const houseId = data.houseId || studentHouseMap.get(data.studentId);
    if (houseId && allTimeTotals[houseId]) {
      allTimeTotals[houseId].negative += 1;
    }
  });

  // 7. Write to housePoints collection
  console.log('\n📊 Results:');
  for (const house of houses) {
    const week = houseTotals[house.id];
    const allTime = allTimeTotals[house.id];
    const weekTotal = week.positive - week.negative;
    const allTimeTotal = allTime.positive - allTime.negative;

    console.log(`   ${house.name}: week=${weekTotal} (${week.positive}↑ ${week.negative}↓) | all-time=${allTimeTotal} (${allTime.positive}↑ ${allTime.negative}↓)`);

    const housePointsRef = doc(db, 'housePoints', house.id);
    await setDoc(housePointsRef, {
      houseId: house.id,
      houseName: house.name,
      schoolId,
      total: allTimeTotal,
      positive: allTime.positive,
      negative: allTime.negative,
      lastUpdated: Timestamp.now(),
      periods: {
        [weekKey]: {
          total: weekTotal,
          positive: week.positive,
          negative: week.negative
        }
      }
    }, { merge: true });
  }

  console.log('\n✅ House points backfilled successfully!');
  console.log('   Refresh your dashboard to see the results.');

  return houseTotals;
}
